import { useState } from 'react'
import { Button, Select } from '@/shared/ui'
import { formatDateTime } from '@/shared/lib/time'
import type { Call } from '@/shared/api/types'
import { useCorrectOutcome, useOutcomes } from '../model'
import styles from './ReportCard.module.css'

interface ReportCardProps {
  call: Call
  /** Показывать ли правку итога руками. */
  editable?: boolean
}

/**
 * Итог разговора.
 *
 * Итог определяет модель, и она ошибается — поэтому его можно поправить.
 * Без этого статистика врёт, а вранью в статистике перестают верить
 * целиком, вместе с верными строками.
 */
export function ReportCard({ call, editable = true }: ReportCardProps) {
  const report = call.report

  if (!report) {
    return (
      <div className={styles.pending}>
        <span className={styles.spinner} />
        {call.status === 'completed'
          ? 'Разбираем разговор — это займёт несколько секунд'
          : 'Отчёта нет: разговор не состоялся'}
      </div>
    )
  }

  return (
    <div className={styles.report}>
      <div className={styles.head}>
        <span className={`${styles.badge} ${badgeClass(report.outcome, report.success)}`}>
          {report.outcomeLabel}
        </span>
        {report.corrected && <span className={styles.corrected}>итог поправлен вручную</span>}

        <div className={styles.score}>
          <span className={styles.scoreValue}>{report.score}</span>
          <span className={styles.scoreMax}>/ 100</span>
        </div>
      </div>

      {report.summary && <p className={styles.summary}>{report.summary}</p>}

      {report.nextActionAt && (
        <div className={styles.next}>
          <span className={styles.nextIcon}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <rect x="2.5" y="3.5" width="13" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
              <path d="M6 1.8V4.6M12 1.8V4.6M2.5 7.2H15.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </span>
          <div>
            <div className={styles.nextTitle}>
              Перезвонить {formatDateTime(report.nextActionAt)}
            </div>
            {report.nextActionNote && (
              <div className={styles.nextNote}>{report.nextActionNote}</div>
            )}
          </div>
        </div>
      )}

      {report.stages?.length ? (
        <div className={styles.stages}>
          <span className={styles.listTitle}>Разговор по этапам</span>
          {report.stages.map((stage, i) => (
            <div key={i} className={styles.stage}>
              <span
                className={`${styles.stageMark} ${
                  stage.done
                    ? styles.stageDone
                    : stage.reached
                      ? styles.stageStuck
                      : styles.stageSkipped
                }`}
              >
                {stage.done ? <StageCheckIcon /> : i + 1}
              </span>
              <div className={styles.stageBody}>
                <div className={styles.stageHead}>
                  <span className={styles.stageTitle}>{stage.title}</span>
                  <span
                    className={`${styles.stageState} ${
                      stage.done ? '' : stage.reached ? styles.stageStateStuck : ''
                    }`}
                  >
                    {stage.done ? 'пройден' : stage.reached ? 'не закрыт' : 'не дошли'}
                  </span>
                </div>
                {stage.note && <div className={styles.stageNote}>{stage.note}</div>}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {(report.objections?.length || report.commitments?.length) && (
        <div className={styles.lists}>
          {report.objections?.length ? (
            <div className={styles.list}>
              <span className={styles.listTitle}>Что возражали</span>
              {report.objections.map((item, i) => (
                <span key={i} className={styles.item}>
                  <span className={styles.bullet}>—</span>
                  {item}
                </span>
              ))}
            </div>
          ) : null}

          {report.commitments?.length ? (
            <div className={styles.list}>
              {/* Обещания важнее возражений: по ним видно, когда агент
                  раздаёт то, чего не должен. */}
              <span className={styles.listTitle}>Что пообещал агент</span>
              {report.commitments.map((item, i) => (
                <span key={i} className={styles.item}>
                  <span className={styles.bullet}>—</span>
                  {item}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      )}

      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Длительность</span>
          <span className={styles.statValue}>{duration(call.durationMs)}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Реплик</span>
          <span className={styles.statValue}>{call.turnsCount}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Перебиваний</span>
          <span className={styles.statValue}>{call.bargeInCount}</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Ответ агента</span>
          <span className={styles.statValue}>
            {call.medianReplyMs > 0 ? `${(call.medianReplyMs / 1000).toFixed(2)} с` : '—'}
          </span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Настроение</span>
          <span className={styles.statValue}>{sentimentLabel(report.sentiment)}</span>
        </div>
      </div>

      {editable && <OutcomeCorrection callId={call.id} current={report.outcome} />}
    </div>
  )
}

function OutcomeCorrection({ callId, current }: { callId: number; current: string }) {
  const [outcome, setOutcome] = useState(current)
  const outcomes = useOutcomes()
  const correct = useCorrectOutcome(callId)

  return (
    <div className={styles.correct}>
      <span className={styles.correctLabel}>Итог определён неверно?</span>
      <Select
        options={outcomes.data?.map((o) => ({ value: o.value, label: o.label })) ?? []}
        value={outcome}
        onChange={(e) => setOutcome(e.target.value)}
        style={{ width: 'auto', minWidth: 220 }}
      />
      <Button
        size="sm"
        variant="secondary"
        disabled={outcome === current}
        loading={correct.isPending}
        onClick={() => correct.mutate({ outcome, note: '' })}
      >
        Исправить
      </Button>
    </div>
  )
}

function StageCheckIcon() {
  return (
    <svg width="9" height="7" viewBox="0 0 9 7" fill="none" aria-hidden="true">
      <path
        d="M1 3.6L3.3 5.8L8 1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function badgeClass(outcome: string, success: boolean): string {
  if (success) return styles.win ?? ''
  if (outcome === 'refusal' || outcome === 'hangup') return styles.loss ?? ''
  if (outcome === 'no_interest' || outcome === 'not_dm') return styles.soft ?? ''
  return styles.plain ?? ''
}

function sentimentLabel(sentiment: string): string {
  if (sentiment === 'positive') return 'Доброжелательное'
  if (sentiment === 'negative') return 'Раздражённое'
  return 'Спокойное'
}

function duration(ms: number): string {
  if (ms <= 0) return '—'
  const seconds = Math.round(ms / 1000)
  const mm = Math.floor(seconds / 60)
  const ss = seconds % 60
  return mm > 0 ? `${mm} мин ${ss} с` : `${ss} с`
}
